/**
 * Knowledge-base citations.
 *
 * When `show_sources` is on, the agent's answer carries inline markers like
 * `[S2]` on sentences drawn from the knowledge base, and a `sources` SSE frame
 * arrives after the text with the entries those markers point at.
 */

/** Inline citation marker, e.g. `[S12]`. */
export const CITATION_PATTERN = /\[(S\d+)\]/g;

/**
 * Internal retrieval score the model is not supposed to echo. Strip it if it
 * ever leaks into the answer text.
 */
const RELEVANCE_SCORE_NOISE = /[ \t]*\[relevance_score\s*=\s*[0-9.]+\]/gi;

export function stripRelevanceScores(text) {
  if (typeof text !== 'string' || text === '') return text;
  if (text.indexOf('relevance_score') === -1) return text;
  return text.replace(RELEVANCE_SCORE_NOISE, '');
}

/**
 * Only `id` is guaranteed on a source — `name` and everything else can be null,
 * so never render the raw value.
 */
export function getSourceLabel(source) {
  const name = source?.name;
  if (typeof name === 'string' && name.trim() !== '') return name.trim();
  return 'Untitled source';
}

/**
 * Map citation id → { source, position }, where position is the 1-based order
 * the source arrived in (sources are already sorted best-first).
 */
export function buildSourceIndex(sources) {
  const index = new Map();
  if (!Array.isArray(sources)) return index;

  sources.forEach((source, i) => {
    const id = source?.id;
    if (typeof id !== 'string' || id === '') return;
    if (index.has(id)) return;
    index.set(id, { source, position: i + 1 });
  });

  return index;
}

/** Elements whose text is verbatim and must keep its markers untouched. */
const OPAQUE_TAGS = new Set(['code', 'pre', 'a', 'script', 'style']);

function splitTextNode(value, sourceIndex) {
  CITATION_PATTERN.lastIndex = 0;

  const parts = [];
  let cursor = 0;
  let matched = false;
  let match = CITATION_PATTERN.exec(value);

  while (match) {
    const id = match[1];
    const entry = sourceIndex.get(id);

    // An unmatched marker (e.g. `[S9]` with three sources) stays plain text —
    // never a broken link.
    if (entry) {
      matched = true;
      if (match.index > cursor) {
        parts.push({ type: 'text', value: value.slice(cursor, match.index) });
      }
      parts.push({
        type: 'element',
        tagName: 'span',
        properties: {
          'data-citation-id': id,
          'data-citation-position': String(entry.position),
        },
        children: [{ type: 'text', value: String(entry.position) }],
      });
      cursor = match.index + match[0].length;
    }

    match = CITATION_PATTERN.exec(value);
  }

  if (!matched) return null;

  if (cursor < value.length) {
    parts.push({ type: 'text', value: value.slice(cursor) });
  }

  return parts;
}

/**
 * rehype plugin that turns resolvable `[Sn]` markers into citation spans.
 *
 * Must run *after* rehype-sanitize, since it introduces elements and data
 * attributes the sanitize schema does not allow. Markers stream in before the
 * `sources` frame does, so with an empty index this is a no-op and the markers
 * upgrade themselves on the next render once sources land.
 */
export function rehypeCitations({ sourceIndex } = {}) {
  return (tree) => {
    if (!sourceIndex || sourceIndex.size === 0) return;

    const walk = (node) => {
      if (!node || !Array.isArray(node.children) || node.children.length === 0) return;
      if (node.type === 'element' && OPAQUE_TAGS.has(node.tagName)) return;

      let replaced = false;
      const next = [];

      for (const child of node.children) {
        if (child?.type === 'text') {
          const parts = splitTextNode(child.value, sourceIndex);
          if (parts) {
            replaced = true;
            next.push(...parts);
            continue;
          }
        } else {
          walk(child);
        }
        next.push(child);
      }

      if (replaced) node.children = next;
    };

    walk(tree);
  };
}
